// Is a News story still current? (#3971; founder, 2026-10-12: "include all
// stories until they are current"). A twin of the desk's rule of record,
// cloud_functions/marketing_agent/desk_proofread.news_currency (and of the
// runner's desk_seo.mjs newsCurrency), pinned on the shared cases in
// tests/fixtures/news_currency_cases.json by __tests__/newsTagsCurrency.test.jsx.
//
// A window (`applies`): from_class / to_class (the high-school classes it
// applies to: fall 2029 entrants are the class of 2029), ends_on (a day after
// which it no longer applies), standing (a policy in force until changed),
// superseded_on / superseded_why. A story with no window falls back to the
// cycle rule: announced in the current admissions cycle (from August 1).

const DAY = /^\d{4}-\d{2}-\d{2}$/;
export const CYCLE_START = [8, 1];
const CLASS_MIN = 2000;
const CLASS_MAX = 2100;
const classYear = (v) => (Number.isInteger(v) && v >= CLASS_MIN && v <= CLASS_MAX ? v : null);
const day = (v) => (DAY.test(String(v || '')) ? String(v) : null);

export function cycleStart(today) {
    const y = Number(String(today).slice(0, 4));
    const start = `${y}-${String(CYCLE_START[0]).padStart(2, '0')}-${String(CYCLE_START[1]).padStart(2, '0')}`;
    return String(today) >= start ? start : `${y - 1}${start.slice(4)}`;
}
// Today on the reader's calendar, as YYYY-MM-DD.
export const localToday = () => new Date().toLocaleDateString('en-CA');
export const applyingClass = (today) => Number(cycleStart(today).slice(0, 4)) + 1;

export function cleanApplies(a) {
    if (!a || typeof a !== 'object' || Array.isArray(a)) return null;
    const out = {
        from_class: classYear(a.from_class), to_class: classYear(a.to_class), ends_on: day(a.ends_on),
        standing: a.standing === true, superseded_on: day(a.superseded_on),
        superseded_why: String(a.superseded_why || '').slice(0, 200) || null, basis: String(a.basis || '').slice(0, 200) || null,
    };
    if (out.from_class && out.to_class && out.to_class < out.from_class) out.to_class = null;
    return out.standing || out.from_class || out.to_class || out.ends_on || out.superseded_on ? out : null;
}

/** {current, why} on `today` (YYYY-MM-DD). */
export function newsCurrency(applies, eventDate, today) {
    const a = cleanApplies(applies);
    const t = String(today);
    if (a && a.superseded_on && a.superseded_on <= t) {
        return { current: false, why: `no longer applies: superseded on ${a.superseded_on}${a.superseded_why ? ` (${a.superseded_why})` : ''}` };
    }
    if (a && a.ends_on && a.ends_on < t) return { current: false, why: `no longer applies: its window closed on ${a.ends_on}` };
    if (a && a.to_class && a.to_class < applyingClass(t)) {
        return { current: false, why: `no longer applies: it applied through the class of ${a.to_class}` };
    }
    if (a && (a.standing || a.from_class || a.to_class || a.ends_on)) {
        if (a.ends_on) return { current: true, why: `current until ${a.ends_on}` };
        if (a.to_class) return { current: true, why: `current through the class of ${a.to_class}` };
        if (a.from_class) return { current: true, why: `current from the class of ${a.from_class}` };
        return { current: true, why: 'current: a standing policy the school has not changed' };
    }
    if (!DAY.test(String(eventDate || ''))) return { current: false, why: 'no window and no announcement date' };
    const start = cycleStart(t);
    const y = Number(start.slice(0, 4));
    const label = `${y}-${String(y + 1).slice(2)}`;
    if (String(eventDate) >= start) return { current: true, why: `announced in the ${label} admissions cycle` };
    return { current: false, why: `no longer news: announced ${eventDate}, before the ${label} admissions cycle began (${start}), with no window that says it still applies` };
}

// A published story's window, from the fields the file already carries: the
// classes in `moves` (Astra Today's, #3753: fromClass / toClass / onlyClass),
// `endsOn`, and `superseded: { on, note, url }` when the school changed it.
export function storyWindow(story) {
    const m = story?.moves || {};
    const only = classYear(m.onlyClass);
    return cleanApplies({
        from_class: only || m.fromClass, to_class: only || m.toClass, ends_on: story?.endsOn,
        superseded_on: story?.superseded?.on, superseded_why: story?.superseded?.note,
    });
}

/** The dated "no longer applies" note a published story shows, or null.
 *  Only a window that says so ends a story: a story with no window is
 *  never marked (absence of a window is not evidence it ended). */
export function noLongerApplies(story, today) {
    const w = storyWindow(story);
    if (!w) return null;
    const { current } = newsCurrency(w, story.eventDate, today);
    if (current) return null;
    if (w.superseded_on && w.superseded_on <= String(today)) {
        return { since: w.superseded_on, changed: true, text: story.superseded?.note || 'The school has changed this since.', url: story.superseded?.url || null };
    }
    if (w.ends_on && w.ends_on < String(today)) {
        const next = new Date(`${w.ends_on}T00:00:00Z`);
        next.setUTCDate(next.getUTCDate() + 1);
        return { since: next.toISOString().slice(0, 10), changed: false, text: 'The date this story is about has passed.', url: null };
    }
    return { since: `${w.to_class}-08-01`, changed: false, text: `This applied to students through the class of ${w.to_class}.`, url: null };
}
